/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { LearnerProfile_SkillAssessmentProficiency as SkillProficiency } from '__generated__/graphql-types';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import { SkillProficiencyTag } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  textBody: () => css`
    margin: var(--cds-spacing-300) 0;
  `,
  skillTag: () => css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

type PropsFromCaller = {
  isOpen: boolean;
  onClose: () => void;
};

type PropsToComponent = PropsFromCaller;

export const SkillsScoreInfoDialog = ({ isOpen, onClose }: PropsToComponent) => {
  return (
    <Dialog data-testid="skills-info-dialog" open={isOpen} onClose={onClose}>
      <Dialog.HeadingGroup>{_t('Skills proficiency')}</Dialog.HeadingGroup>
      <Dialog.Content>
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          requireFullyVisible={false}
          // not tracked in eventing v3
          trackingName="profile_skills_assessment_info_dialog"
        >
          <Typography2 variant="bodyPrimary" component="p">
            {_t(
              'This is determined by an industry-leading, role-based skills assessment. It doesn’t reflect other assessments taken on Coursera.'
            )}
          </Typography2>
          <Typography2 variant="bodyPrimary" component="p" css={styles.textBody}>
            {_t(
              'Skills proficiency is ranked at one of four levels, which are based on industry standards for an entry-level role.'
            )}
          </Typography2>
          <SkillProficiencyTag
            proficiency={SkillProficiency.Beginner}
            boldedContent={SkillProficiency.Beginner.toLowerCase()}
            regularContent="(0% - 25%)"
            customCss={styles.skillTag}
          />
          <SkillProficiencyTag
            proficiency={SkillProficiency.Intermediate}
            boldedContent={SkillProficiency.Intermediate.toLowerCase()}
            regularContent="(26% - 50%)"
            customCss={styles.skillTag}
          />
          <SkillProficiencyTag
            proficiency={SkillProficiency.Experienced}
            boldedContent={SkillProficiency.Experienced.toLowerCase()}
            regularContent="(51% - 75%)"
            customCss={styles.skillTag}
          />
          <SkillProficiencyTag
            proficiency={SkillProficiency.Proficient}
            boldedContent={SkillProficiency.Proficient.toLowerCase()}
            regularContent="(76% - 100%)"
          />
        </TrackedDiv>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          component={TrackedButton}
          requireFullyVisible={false}
          withVisibilityTracking={true}
          // not tracked in eventing v3
          trackingName="profile_close_skills_assessment_dialog"
          onClick={onClose}
        >
          {_t('Got it')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default SkillsScoreInfoDialog;
